// Tiny localStorage layer for stale-while-revalidate API caching + offline
// write queue. Designed for the Sophi App's data shapes (events list, balance,
// profile, blocks, offers — all small JSON, ~10-50KB total budget).
//
// Why localStorage, not IndexedDB:
//   - Data fits comfortably in localStorage's 5MB quota
//   - Synchronous reads → no flash of empty UI on cold open
//   - Simpler API, smaller bundle, easier incident response
// IndexedDB would only be necessary if we needed queryable structured data
// (we don't — we're caching whole API responses by URL).
//
// Schema versioning: the CACHE_VERSION prefix lets a deploy invalidate ALL
// cached data by bumping the version. Bump when an API response shape changes
// in a backwards-incompatible way.

const CACHE_VERSION = 'v1';
const CACHE_PREFIX = `sophi-cache:${CACHE_VERSION}:`;
const QUEUE_KEY = `sophi-queue:${CACHE_VERSION}`;

// ============================================================================
// SWR cache (read side)
// ============================================================================

interface CacheEntry<T> {
  v: T;
  t: number;       // ISO ms when cached
  ttl: number;     // ms TTL
}

export function getCached<T>(key: string): { value: T; stale: boolean } | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw) as CacheEntry<T>;
    const age = Date.now() - entry.t;
    return { value: entry.v, stale: age > entry.ttl };
  } catch {
    return null;
  }
}

export function setCached<T>(key: string, value: T, ttlMs: number): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const entry: CacheEntry<T> = { v: value, t: Date.now(), ttl: ttlMs };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(entry));
  } catch {
    // Quota exceeded or private browsing — silently degrade. The app still
    // works, just without the speed-up on next cold open.
  }
}

export function invalidateCached(...keys: string[]): void {
  if (typeof localStorage === 'undefined') return;
  for (const key of keys) {
    try { localStorage.removeItem(CACHE_PREFIX + key); } catch { /* ignore */ }
  }
}

/** Clear ALL cached data (e.g. on session refresh / logout). */
export function clearAllCache(): void {
  if (typeof localStorage === 'undefined') return;
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && (k.startsWith(CACHE_PREFIX) || k.startsWith(`sophi-cache:`))) {
      toRemove.push(k);
    }
  }
  toRemove.forEach(k => localStorage.removeItem(k));
}

// ============================================================================
// Offline write queue (specifically for check-in)
// ============================================================================
//
// When a write fails because the network is unavailable, queue the request
// with the CLIENT timestamp captured at the moment of user intent. The retry
// drains the queue when the browser fires the 'online' event OR when the app
// reopens with connectivity. The server accepts a `clientTimestamp` field so
// a check-in synced 2h after the user actually arrived still records the
// real arrival time — not the sync time.

export type QueuedRequestKind = 'checkin';

export interface QueuedCheckIn {
  kind: 'checkin';
  eventId: string;
  clientTimestamp: string;     // ISO — when the user actually tapped
  geo?: { lat?: number; lng?: number };
  attempts: number;            // how many sync attempts made
  queuedAt: string;            // ISO — when added to queue (== clientTimestamp on first add)
}

export type QueuedRequest = QueuedCheckIn;

function readQueue(): QueuedRequest[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as QueuedRequest[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(items: QueuedRequest[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items));
  } catch {
    /* quota / private mode — last-resort: drop oldest if full */
  }
}

export function enqueueCheckIn(input: { eventId: string; geo?: { lat?: number; lng?: number } }): QueuedCheckIn {
  const queue = readQueue();
  const now = new Date().toISOString();
  // Idempotency: if a check-in for this event is already queued, return the
  // existing entry instead of stacking duplicates. The user might tap again
  // before the queue drains.
  const existing = queue.find(q => q.kind === 'checkin' && q.eventId === input.eventId) as QueuedCheckIn | undefined;
  if (existing) return existing;

  const entry: QueuedCheckIn = {
    kind: 'checkin',
    eventId: input.eventId,
    clientTimestamp: now,
    geo: input.geo,
    attempts: 0,
    queuedAt: now,
  };
  queue.push(entry);
  writeQueue(queue);
  return entry;
}

export function listQueue(): QueuedRequest[] {
  return readQueue();
}

export function isCheckInQueued(eventId: string): boolean {
  return readQueue().some(q => q.kind === 'checkin' && q.eventId === eventId);
}

export function removeFromQueue(predicate: (item: QueuedRequest) => boolean): void {
  const queue = readQueue();
  const next = queue.filter(item => !predicate(item));
  if (next.length !== queue.length) writeQueue(next);
}

export function bumpAttempts(predicate: (item: QueuedRequest) => boolean): void {
  const queue = readQueue();
  let changed = false;
  for (const item of queue) {
    if (predicate(item)) {
      item.attempts++;
      changed = true;
    }
  }
  if (changed) writeQueue(queue);
}

// ============================================================================
// Network-state event helpers
// ============================================================================

export function isOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine;
}

/** Fire `cb` whenever the browser transitions back to online. Returns unsubscribe fn. */
export function onOnline(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('online', cb);
  return () => window.removeEventListener('online', cb);
}
